import { Button, Modal as BzModal, ModalContent, Input } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { useUser } from '@benzinga/user-context';
import { UserManager } from '@benzinga/user-manager';
import { isEmpty, validateUSAddress } from '@benzinga/utils';
import React from 'react';
import styled from 'styled-components';
import styles from '../scss/component.module.scss';
import { Fallback } from './Fallback';
import { useOpraDeadline } from '../useOpraDeadline';
interface UserInfoModalProps {
  parentRef: React.RefObject<HTMLDivElement | null>;
  children?: React.ReactNode;
}

let opraModalRefreshDismissCount = 0;
export const UserInfoModal: React.FC<UserInfoModalProps> = props => {
  const user = useUser();
  const OPRA_MODAL_MAX_DISMISSES = 5;
  const session = React.useContext(SessionContext);
  const userManager = session.getManager(UserManager);
  const { getModalCountdown, initMandatoryTimeStamp, isUserActionModalMandatory } = useOpraDeadline();
  const mandatory = isUserActionModalMandatory();
  const countDownText = getModalCountdown();
  const [visible, setVisible] = React.useState<boolean | null>(null);
  const [shippingAddress, setShippingAddress] = React.useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const opraModalSessionDismissCount = opraModalRefreshDismissCount;
  const isValidAddress = React.useMemo(() => validateUSAddress(shippingAddress ?? ''), [shippingAddress]);

  /**
   * mandatory- 14-days hard limit
   * logic- always show if (mandatory + empty shipping_address) else check if empty shipping_address and user has dismissed modal < 6 times in current session
   */
  const shouldDisplayOPRAModal = React.useMemo(
    () =>
      (mandatory && isEmpty(user?.shippingAddress)) ||
      (isEmpty(user?.shippingAddress) && opraModalSessionDismissCount < OPRA_MODAL_MAX_DISMISSES),
    [mandatory, opraModalSessionDismissCount, user?.shippingAddress],
  );

  const localStateCleanups = React.useCallback(() => {
    setIsSubmitting(false);
    setVisible(false);
    //updating in-memory close modal count key
    opraModalRefreshDismissCount = OPRA_MODAL_MAX_DISMISSES;
  }, []);

  const handleUpdateProfile = React.useCallback(async () => {
    if (!shippingAddress || isSubmitting) return;
    setIsSubmitting(true);
    if (!isValidAddress) {
      setIsSubmitting(false);
      return;
    }
    try {
      await userManager.editUser({ shipping_address: shippingAddress });
      localStateCleanups();
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  }, [shippingAddress, isSubmitting, isValidAddress, userManager, localStateCleanups]);
  const handleModalClose = React.useCallback(() => {
    setVisible(false);
    opraModalRefreshDismissCount += 1;
  }, []);

  const handleModalState = React.useCallback((visible: boolean) => setVisible(visible), []);

  React.useEffect(() => {
    initMandatoryTimeStamp();
  }, [initMandatoryTimeStamp]);

  React.useEffect(() => {
    handleModalState(shouldDisplayOPRAModal);
  }, [handleModalState, shouldDisplayOPRAModal]);

  if (!user && opraModalSessionDismissCount >= OPRA_MODAL_MAX_DISMISSES) return props.children;

  return (
    <div className={styles.container}>
      {shouldDisplayOPRAModal ? (
        visible === null ? null : visible ? (
          <BzModal
            doNotCloseOnBackgroundClick
            hideCrossBtn={false}
            onClose={handleModalClose}
            parentRef={props?.parentRef?.current ?? undefined}
            positionUpperThird
            size={{ alert: true }}
            title="Information Update Request"
            visible={visible}
          >
            <ModalContent className={styles.content}>
              <h5>
                OPRA (Options Price Reporting Authority) needs to know the following information about you before you
                can access Options data. For more information, visit{' '}
                <a href="https://www.opraplan.com/faqs" rel="noreferrer" target="_blank">
                  https://www.opraplan.com/faqs
                </a>
              </h5>
              <label className={styles.label}>Please enter your full Mailing Address</label>
              <Input
                onChange={setShippingAddress}
                placeholder="123 N Main St Apt 4B, Chicago, IL 60601, USA"
                value={shippingAddress ?? ''}
              />
              <p className={styles.countdown}>Note: {countDownText} </p>
              <ValidationText $valid={isValidAddress}>
                {shippingAddress && !isValidAddress ? 'Please enter a valid full mailing address' : ''}
              </ValidationText>
            </ModalContent>
            <Submit
              disabled={!shippingAddress || !isValidAddress}
              isLoading={isSubmitting}
              onClick={handleUpdateProfile}
              size="md"
            >
              Submit
            </Submit>
          </BzModal>
        ) : mandatory ? (
          <Fallback onClick={() => handleModalState(true)} />
        ) : (
          props.children
        )
      ) : (
        props.children
      )}
    </div>
  );
};

const Submit = styled(Button)`
  background-color: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.foregroundActive};
  &:disabled {
    color: ${({ theme }) => theme.colors.foregroundActive};
  }
`;

const ValidationText = styled.span<{ $valid: boolean }>`
  color: ${({ $valid, theme }) => ($valid ? theme.colors.success : theme.colors.danger)};
`;
